import cn from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import useSWR, { preload } from 'swr';

import { fetcher } from '@/api';
import { disableScroll, enableScroll } from '@/helpers/common';
import { Menu as MenuData } from '@/types/menu';
import uuid from '@/utils/uuid';

import styles from './styles.module.scss';
import TopLevelMenuItem from './TopLevelMenuItem';

type Props = {
  slot?: string;
  className?: string;
};

// Export preload function for use in processors
export const preloadMenu = (slot: string) => {
  return preload(`/content/public/menu/${slot}`, fetcher);
};

const Menu: React.FC<Props> = ({ slot, className }) => {
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [activeMenu, setActiveMenu] = useState<string | undefined>(undefined);
  const [activeSubMenu, setActiveSubMenu] = useState<string | undefined>(undefined);
  const [menuOpened, setMenuOpened] = useState<boolean>(false);

  const { data, isLoading } = useSWR<MenuData>(slot ? `/content/public/menu/${slot}` : null, fetcher);

  const setHeaderHeight = useCallback(() => {
    const dropdown = dropdownRef.current;
    if (dropdown) {
      const vh = window.innerHeight * 0.01;
      dropdown.style.setProperty('--vh', `${vh}px`);
    }
  }, []);

  const openMenu = useCallback(() => {
    disableScroll();
    setHeaderHeight();
    setMenuOpened(true);
    const ckyButtons = document.querySelector('body > div[class^="cky-btn-"]');
    if (ckyButtons) {
      ckyButtons.classList.add('erom-visually-hidden');
    }
  }, [setHeaderHeight]);

  const closeMenu = useCallback(() => {
    setMenuOpened(false);
    enableScroll();
    const ckyButtons = document.querySelector('body > div[class^="cky-btn-"]');
    if (ckyButtons) {
      ckyButtons.classList.remove('erom-visually-hidden');
    }
  }, []);

  const toggleMenu = useCallback(() => {
    if (menuOpened) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [menuOpened, openMenu, closeMenu]);

  const handleLinkClick = useCallback(() => {
    setActiveMenu(undefined);
    setActiveSubMenu(undefined);
  }, []);

  useEffect(() => {
    return () => {
      // Cleanup function to close the menu when the component unmounts
      closeMenu();
    };
  }, [closeMenu]);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      setHeaderHeight();
    });

    observer.observe(document.body);

    return () => {
      observer.disconnect();
    };
  }, [setHeaderHeight]);

  if (isLoading) return null;

  const id = uuid.v4('menu');

  return (
    <FocusTrap active={menuOpened} focusTrapOptions={{ onPostDeactivate: () => closeMenu() }}>
      <div className={cn(styles.menu, className)}>
        {menuOpened && <div className={cn(styles['cover'], 'cover')} role="none" onMouseDown={closeMenu} />}
        <button
          id={`${id}-trigger`}
          className="menu-trigger"
          onClick={toggleMenu}
          aria-expanded={menuOpened}
          aria-controls={`${id}-menu`}
        >
          Menu
          {!menuOpened ? <i className="fa-solid fa-bars-sort"></i> : <i className="fa-solid fa-times"></i>}
        </button>
        <div
          id={`${id}-menu`}
          role="region"
          ref={dropdownRef}
          aria-labelledby={`${id}-trigger`}
          className={cn('menu-dropdown', { active: menuOpened })}
        >
          <ul className="menu">
            {data?.items?.map(item => (
              <TopLevelMenuItem
                key={item.id}
                item={item}
                activeMenu={activeMenu}
                activeSubMenu={activeSubMenu}
                onMenuToggle={setActiveMenu}
                onSubMenuToggle={setActiveSubMenu}
                onLinkClick={handleLinkClick}
              />
            ))}
          </ul>
        </div>
      </div>
    </FocusTrap>
  );
};

export default Menu;
